<template>
  <v-dialog
    v-model="internalValue"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    content-class="media-viewer-dialog"
  >
    <div class="mv-container" :class="{ 'mv-fullscreen': isFullscreen }">
      <!-- Toolbar -->
      <div class="mv-toolbar">
        <div class="mv-toolbar-left">
          <div>
            <div class="mv-title">
              {{ formatFileName(activeItem ? activeItem.name : "Media Preview") }}
            </div>
            <small style="opacity:.7">
              {{ currentIndex + 1 }} of {{ items.length }}
            </small>
          </div>
        </div>
        <div class="mv-toolbar-right">
          <button class="mv-btn" @click="downloadMedia" title="Download">
            <v-icon size="20">mdi-tray-arrow-down</v-icon>
          </button>
          <button class="mv-btn" @click="toggleFullscreen" title="Fullscreen">
            <v-icon size="20">mdi-fullscreen</v-icon>
          </button>
          <button class="mv-btn" @click="internalValue = false" title="Close">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="mv-content">
        <button
          v-if="items.length > 1"
          class="mv-nav-btn mv-prev"
          @click="prev"
          :disabled="currentIndex === 0"
        >
          <v-icon size="24">mdi-arrow-left</v-icon>
        </button>

        <div class="mv-media-wrap" :class="{ 'mv-pdf-mode': activeItem && activeItem.type === 'document' }">
          <transition name="fade" mode="out-in">
            <div :key="currentIndex" class="mv-media-inner" :class="{ 'mv-pdf-mode': activeItem && activeItem.type === 'document' }">
              <img
                v-if="activeItem && activeItem.type === 'image'"
                :src="activeItem.url"
                class="mv-image"
                :style="imageStyle"
              />
              <video
                v-else-if="activeItem && activeItem.type === 'video'"
                :src="activeItem.url"
                controls
                autoplay
                class="mv-video"
                :style="imageStyle"
              ></video>
              <iframe
                v-else-if="activeItem && activeItem.type === 'document'"
                :src="activeItem.url"
                class="mv-pdf-iframe"
                frameborder="0"
              ></iframe>
            </div>
          </transition>
        </div>

        <button
          v-if="items.length > 1"
          class="mv-nav-btn mv-next"
          @click="next"
          :disabled="currentIndex === items.length - 1"
        >
          <v-icon size="24">mdi-arrow-right</v-icon>
        </button>
      </div>

      <!-- Bottom controls -->
      <div
        class="mv-footer-controls"
        v-if="activeItem && activeItem.type !== 'document'"
      >
        <div class="mv-zoom-toolbar">
          <button class="mv-zoom-btn" @click="zoomOut" title="Zoom Out">
            <v-icon size="20">mdi-magnify-minus</v-icon>
          </button>
          <div class="mv-zoom-divider"></div>
          <button class="mv-zoom-btn" @click="resetZoom" title="Reset Zoom">
            <v-icon size="20">mdi-backup-restore</v-icon>
          </button>
          <div class="mv-zoom-divider"></div>
          <button class="mv-zoom-btn" @click="zoomIn" title="Zoom In">
            <v-icon size="20">mdi-magnify-plus</v-icon>
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: "MediaViewer",
  props: {
    value: Boolean,
    items: {
      type: Array,
      default: () => [], // Array of { url, type: 'image'|'video'|'document', name }
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isFullscreen: false,
      currentIndex: this.index,
      zoomLevel: 1,
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    activeItem() {
      return this.items[this.currentIndex] || null;
    },
    imageStyle() {
      return {
        transform: `scale(${this.zoomLevel})`,
      };
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.currentIndex = this.index;
        this.resetZoom();
      }
    },
    index(newVal) {
      this.currentIndex = newVal;
      this.resetZoom();
    },
  },
  methods: {
    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.resetZoom();
      }
    },
    next() {
      if (this.currentIndex < this.items.length - 1) {
        this.currentIndex++;
        this.resetZoom();
      }
    },
    zoomIn() {
      if (this.zoomLevel < 4) this.zoomLevel += 0.25;
    },
    zoomOut() {
      if (this.zoomLevel > 0.5) this.zoomLevel -= 0.25;
    },
    resetZoom() {
      this.zoomLevel = 1;
    },
    async downloadMedia() {
      if (!this.activeItem) return;
      const url = this.activeItem.url;
      const name = this.activeItem.name || "download";

      /**
       * saveBlob — cross-browser safe file download.
       * Key Edge fixes:
       *  1. Wrap as application/octet-stream so browser won't open inline
       *  2. Append link to <html> (document.documentElement), not <body>,
       *     to escape Vuetify dialog stacking context in Edge
       *  3. Use dispatchEvent(MouseEvent) instead of .click() — more
       *     reliable inside fullscreen dialogs in Edge
       *  4. setAttribute("download") instead of link.download = ...
       */
      const saveBlob = (blob, fileName) => {
        // Legacy Edge (pre-Chromium) / IE11
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, fileName);
          return;
        }

        // Force octet-stream — prevents Edge/Chrome opening image inline
        const forceBlob = new Blob([blob], { type: "application/octet-stream" });
        const objectUrl = URL.createObjectURL(forceBlob);

        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", fileName);
        // Position off-screen so it doesn't affect layout
        link.style.cssText =
          "position:fixed;top:-9999px;left:-9999px;opacity:0;";

        // Append to <html> to escape any dialog stacking context in Edge
        document.documentElement.appendChild(link);

        // dispatchEvent is more reliable than .click() inside dialogs in Edge
        link.dispatchEvent(
          new MouseEvent("click", {
            bubbles: false,
            cancelable: true,
            view: window,
          })
        );

        document.documentElement.removeChild(link);
        // Give Edge extra time before revoking
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      };

      // blob: URL — re-fetch so we can force octet-stream type
      if (url && url.startsWith("blob:")) {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          saveBlob(blob, name);
        } catch (e) {
          console.error("[MediaViewer] blob fetch failed:", e);
        }
        return;
      }

      // Try native fetch first (avoids axios interceptors that may interfere)
      try {
        const res = await fetch(url, { method: "GET", credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        saveBlob(blob, name);
        return;
      } catch (fetchErr) {
        console.warn("[MediaViewer] fetch failed, trying axios:", fetchErr);
      }

      // Axios fallback
      try {
        const response = await this.$axios.get(url, { responseType: "blob" });
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: "application/octet-stream" });
        saveBlob(blob, name);
      } catch (axiosErr) {
        console.error("[MediaViewer] Download failed:", axiosErr);
        // Avoid window.open (opens new tab in Edge) — alert user instead
        alert(
          "Download failed. Please right-click the image and choose 'Save image as…'"
        );
      }
    },
    openInNewTab(url) {
      window.open(url, "_blank");
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.log(`Error: ${err.message}`);
        });
      } else {
        document.exitFullscreen();
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
    formatFileName(name) {
      if (!name) return "Media Preview";
      return name.split("/").pop();
    },
  },
  mounted() {
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
  },
};
</script>

<style scoped>
/* =========================
   ROOT CONTAINER
========================= */

.mv-container {
  width: 96%;
  height: 94%;
  margin: auto;
  margin-top: 20px;

  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  border-radius: 20px;

  background: var(--mv-surface);
  color: var(--mv-text);

  border: 1px solid var(--mv-border);

  box-shadow:
    0 25px 50px rgba(0, 0, 0, 0.18),
    0 10px 20px rgba(0, 0, 0, 0.08);

  backdrop-filter: blur(20px);
}

.mv-container.mv-fullscreen {
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
  border-radius: 0 !important;
}

/* =========================
   TOOLBAR
========================= */

.mv-toolbar {
  height: 72px;
  min-height: 72px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 0 24px;

  background: var(--mv-card);

  border-bottom: 1px solid var(--mv-border);

  z-index: 20;
}

.mv-toolbar-left {
  display: flex;
  align-items: center;
}

.mv-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--mv-text);

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mv-toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* =========================
   ACTION BUTTONS
========================= */

.mv-btn {
  width: 42px;
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 12px;

  background: var(--mv-hover);

  transition: all 0.25s ease;
}

.mv-btn:hover {
  transform: translateY(-2px);
  background: var(--mv-hover-active);
}
.mv-btn {
  color: var(--mv-text);
}
.mv-btn .v-icon,
.mv-nav-btn .v-icon,
.mv-zoom-btn .v-icon {
  color: var(--btn--color) !important;
}

/* =========================
   CONTENT
========================= */

.mv-content {
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  position: relative;

  overflow: hidden;
  min-height: 0;

  background: var(--mv-viewer-bg);

  padding: 24px;
}

/* =========================
   NAVIGATION BUTTONS
========================= */

.mv-nav-btn {
  width: 56px;
  height: 56px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: var(--mv-card);

  border: 1px solid var(--mv-border);

  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.12);

  transition: all 0.25s ease;

  z-index: 10;
}

.mv-nav-btn:hover:not(:disabled) {
  transform: scale(1.08);
}

.mv-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mv-prev {
  margin-right: 20px;
}

.mv-next {
  margin-left: 20px;
}

/* =========================
   MEDIA WRAPPER
========================= */

.mv-media-wrap {
  flex: 1;

  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  padding: 24px;
}

.mv-media-inner {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;
}

/* =========================
   IMAGE
========================= */

.mv-image {
  max-width: 100%;
  max-height: 100%;

  object-fit: contain;

  border-radius: 16px;

  background: white;

  transition: transform 0.25s ease;

  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.15);
}

/* =========================
   VIDEO
========================= */

.mv-video {
  max-width: 100%;
  max-height: 100%;

  border-radius: 16px;

  background: #000;

  outline: none;

  transition: transform 0.25s ease;

  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.15);
}

/* =========================
   PDF
========================= */

.mv-media-wrap.mv-pdf-mode {
  padding: 0;
}

.mv-media-inner.mv-pdf-mode {
  width: 100%;
  height: 100%;
}

.mv-pdf-iframe {
  width: 100%;
  height: 100%;

  border: none;
  background: white;
}

/* =========================
   FOOTER ZOOM CONTROLS
========================= */

.mv-footer-controls {
  position: absolute;

  bottom: 24px;
  left: 50%;

  transform: translateX(-50%);

  z-index: 20;
}

.mv-zoom-toolbar {
  display: flex;
  align-items: center;

  padding: 6px;

  border-radius: 16px;

  background: var(--mv-card);

  border: 1px solid var(--mv-border);

  backdrop-filter: blur(20px);

  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.18);
}

.mv-zoom-btn {
  width: 46px;
  height: 46px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 10px;

  transition: all 0.25s ease;
}

.mv-zoom-btn:hover {
  background: var(--mv-hover);
}

.mv-zoom-divider {
  width: 1px;
  height: 24px;

  margin: 0 6px;

  background: var(--mv-border);
}

/* =========================
   ANIMATION
========================= */

.fade-enter-active,
.fade-leave-active {
  transition: all 0.25s ease;
}

.fade-enter {
  opacity: 0;
  transform: scale(0.97);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(1.03);
}

/* =========================
   RESPONSIVE
========================= */

@media (max-width: 768px) {
  .mv-container {
    width: 100%;
    height: 100%;
    margin: 0;
    border-radius: 0;
  }

  .mv-toolbar {
    padding: 0 12px;
  }

  .mv-title {
    font-size: 15px;
  }

  .mv-nav-btn {
    width: 48px;
    height: 48px;
  }

  .mv-content {
    padding: 12px;
  }

  .mv-media-wrap {
    padding: 12px;
  }
}

/* =========================
   LIGHT THEME
========================= */

.theme--light {
  --mv-surface: rgba(255, 255, 255, 0.92);
  --mv-card: rgba(248, 250, 252, 0.95);
  --mv-text: #172b4d;
  --mv-border: rgba(229, 231, 235, 0.6);
  --mv-hover: rgba(0, 0, 0, 0.05);
  --mv-hover-active: rgba(0, 0, 0, 0.1);
  --mv-viewer-bg: #f1f5f9;
}

/* =========================
   DARK THEME
========================= */

.theme--dark {
  --mv-surface: #1e293b;
  --mv-card: #273548;
  --mv-text: #f8fafc;
  --mv-border: rgba(255, 255, 255, 0.1);
  --mv-hover: rgba(255, 255, 255, 0.06);
  --mv-hover-active: rgba(255, 255, 255, 0.12);
  --mv-viewer-bg: #1e293b;
}
</style>

<style>
.media-viewer-dialog {
  margin: 0 !important;
  backdrop-filter: blur(8px) !important;
  -webkit-backdrop-filter: blur(8px) !important;
}

.theme--light .media-viewer-dialog {
  background: rgba(241, 245, 249, 0.85) !important;
}

.theme--dark .media-viewer-dialog {
  background: rgba(15, 23, 42, 0.75) !important;
}
</style>